import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Redirect } from 'react-router-dom';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { useFeatureDetails, useViewerRoles } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import {
	Button,
	ClipboardButton,
	Container,
	GraphQLErrorMessage,
	Heading,
	Heading3,
	Input,
	Paragraph,
	Pill,
	Separator,
	Switch,
	UnorderedList,
	tokens,
	useToasts,
} from '@treams/treams-ui';

import ProductTile from '../ProductTile';

const ASSISTANT_LOGO_URLS = [
	'https://assets.ontreams.com/marketplace/copilot.png',
	'https://assets.ontreams.com/marketplace/chatgpt.png',
	'https://assets.ontreams.com/marketplace/claude.png',
];

const Banner = styled(Box)`
	background: linear-gradient(135deg, #f4f8fb 0%, #e7eef5 100%);
	border: 1px solid ${tokens.border.subtle};
	border-radius: 8px;
	height: 200px;
	display: flex;
	align-items: center;
	justify-content: center;
	overflow: hidden;
	gap: 32px;

	img {
		max-height: 72px;
		max-width: 96px;
		object-fit: contain;
	}
`;

const ConfigCard = styled(Box)`
	border: 1px solid ${tokens.border.subtle};
	border-radius: 6px;
	background: ${(props) => props.theme.colors.white};
	padding: 20px 24px;
`;

const ActivePill = styled(Pill)`
	background-color: ${(props) => props.theme.colors.green};
	color: ${(props) => props.theme.colors.white};
`;

const InactivePill = styled(Pill)`
	background-color: ${tokens.border.subtle};
	color: ${tokens.text.secondary};
`;

/**
 * The API exposes the connector to workspace superusers only
 * (`McpRemote.manageMcpRemote`), so the tile and the page follow that.
 */
export function useCanManageMcpRemote(): { canManage: boolean; loading: boolean } {
	const { loading: rolesLoading, isWorkspaceSuperuser } = useViewerRoles();
	const { available, loading: featureLoading } = useFeatureDetails(graphql.FeatureName.ExternalMcp);

	return {
		canManage: isWorkspaceSuperuser && !!available,
		loading: rolesLoading || featureLoading,
	};
}

function Description() {
	const { t } = useTranslation();

	return (
		<>
			<Paragraph mb={2}>{t('settings.connectors.mcpRemote.description.intro')}</Paragraph>
			<Paragraph mb={3}>{t('settings.connectors.mcpRemote.description.auth')}</Paragraph>
			<Heading3>{t('settings.connectors.mcpRemote.description.benefitsTitle')}</Heading3>
			<Paragraph>
				<UnorderedList>
					{t('settings.connectors.mcpRemote.benefits')
						.split('\n')
						.map((benefit) => benefit.trim())
						.filter(Boolean)
						.map((benefit) => (
							<li key={benefit}>{benefit}</li>
						))}
				</UnorderedList>
			</Paragraph>
		</>
	);
}

/**
 * Customer-facing remote MCP integrations page. Employees authenticate against the
 * workspace's own Cognito user pool, so there is nothing to configure beyond enabling
 * the connector and sharing the connection URL. Gated by the EXTERNAL_MCP feature flag.
 */
export default function McpRemote() {
	const { t } = useTranslation();
	const { addToast } = useToasts();
	const { canManage, loading: accessLoading } = useCanManageMcpRemote();

	const query = graphql.useMcpRemoteSettingsQuery({
		fetchPolicy: 'cache-and-network',
		skip: accessLoading || !canManage,
	});

	const settings = query.data?.mcpRemoteSettings;

	const [enabled, setEnabled] = useState(false);

	useEffect(() => {
		setEnabled(settings?.enabled ?? false);
	}, [settings?.enabled]);

	const [save, { loading: saving, error: saveError }] =
		graphql.useUpdateMcpRemoteSettingsMutation();

	const handleSave = useCallback(async () => {
		try {
			await save({
				variables: { enabled },
				refetchQueries: ['McpRemoteSettings'],
			});
			addToast(t('common.saved'), { appearance: 'success', autoDismiss: true });
		} catch {
			// Error surfaced via saveError.
		}
	}, [save, enabled, addToast, t]);

	if (accessLoading) {
		return null;
	}

	if (!canManage) {
		return <Redirect to="/settings/integrations" />;
	}

	const isActive = !!settings?.enabled;
	const canSave = !!settings && !saving && !query.loading && enabled !== settings.enabled;

	return (
		<Container py={4}>
			<Banner mb={3}>
				{ASSISTANT_LOGO_URLS.map((url) => (
					<img key={url} src={url} alt="" />
				))}
			</Banner>

			<Flex mb={3} alignItems="center">
				<Heading flexGrow={1} flexShrink={1}>
					{t('settings.connectors.mcpRemote.title')}
				</Heading>
				<Box flexGrow={0} flexShrink={0}>
					{isActive ? (
						<ActivePill small>{t('settings.connectors.mcpRemote.status.active')}</ActivePill>
					) : (
						<InactivePill small>{t('settings.connectors.mcpRemote.status.inactive')}</InactivePill>
					)}
				</Box>
			</Flex>

			<Flex mb={4} style={{ gap: 24 }}>
				<Box width={[1, 1, 1 / 2]}>
					<Heading3 mb={1}>{t('settings.connectors.mcpRemote.description.heading')}</Heading3>
					<Separator mt={2} mb={3} />
					<Description />
					<Paragraph mb={2}>
						<a
							href="https://modelcontextprotocol.io/"
							target="_blank"
							rel="noopener noreferrer"
							style={{ textDecoration: 'underline' }}
						>
							{t('settings.connectors.mcpRemote.viewWebsite')}
						</a>
					</Paragraph>
				</Box>

				<Box width={[1, 1, 1 / 2]}>
					<Heading3 mb={1}>{t('settings.connectors.mcpRemote.config.title')}</Heading3>
					<Separator mt={2} mb={3} />
					{saveError && (
						<Box mb={2}>
							<GraphQLErrorMessage error={saveError.graphQLErrors?.[0] ?? saveError} />
						</Box>
					)}
					<ConfigCard>
						<Flex alignItems="center" mb={2}>
							<Box flexGrow={1}>
								<Paragraph>{t('settings.connectors.mcpRemote.config.enabled')}</Paragraph>
							</Box>
							<Switch on={enabled} onChange={setEnabled} />
						</Flex>
						<Paragraph mb={2} small color="grey">
							{t('settings.connectors.mcpRemote.config.enabled.hint')}
						</Paragraph>

						{settings && isActive && (
							<Box mb={2}>
								<Paragraph mb={1}>
									{t('settings.connectors.mcpRemote.config.connectionUrl')}
								</Paragraph>
								<Paragraph mb={2} small color="grey">
									{t('settings.connectors.mcpRemote.config.connectionUrl.hint')}
								</Paragraph>
								<Flex alignItems="center">
									<Input value={settings.connectionUrl} disabled onChange={() => {}} />
									<ClipboardButton text={settings.connectionUrl} ml={2} />
								</Flex>
							</Box>
						)}

						<Flex justifyContent="flex-end" mb={2}>
							<Button
								small
								type="primary"
								title={t('common.save')}
								disabled={!canSave}
								loading={saving}
								onClick={handleSave}
							/>
						</Flex>
					</ConfigCard>
				</Box>
			</Flex>
		</Container>
	);
}

export function McpRemoteTile(props: { selected: boolean }) {
	const { t } = useTranslation();

	return (
		<ProductTile
			id="mcp"
			title={t('settings.connectors.mcpRemote.title')}
			description={t('settings.connectors.mcpRemote.tile.description')}
			logoImageURLs={ASSISTANT_LOGO_URLS}
			selected={props.selected}
		/>
	);
}
